import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { eachDayOfInterval, format, isWeekend, parseISO } from 'date-fns';
import { memo, ReactNode } from 'react';
import { HoursByEmployeeAndDayLine, HoursByEmployeeAndDayRow } from 'types/reporting/performance';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { useTimecardPrecision } from 'utils/useTimecardPrecision';
import { employeeLabel, formatReportNumber, ReportEmployeeLink } from '../reportCells';

const SECONDS_PER_HOUR = 3600;
/** The two label columns stay in view while the dates scroll under them. */
const EMPLOYEE_COLUMN = 'bg-paper sticky left-0 w-56 min-w-56 max-w-56 whitespace-normal';
const LINE_COLUMN = 'bg-paper sticky left-56 w-52 min-w-52 border-r';

type Day = { key: string; isWeekend: boolean; isFuture: boolean };

/** The period's dates, oldest first: a date after today without hours shows none rather than 0. */
function daysOf(from: string, to: string, today: Date): Day[] {
    const todayKey = format(today, 'yyyy-MM-dd');
    return eachDayOfInterval({ start: parseISO(from), end: parseISO(to) }).map((date) => {
        const key = format(date, 'yyyy-MM-dd');
        return { key, isWeekend: isWeekend(date), isFuture: key > todayKey };
    });
}

/**
 * The Hours by employee and day pivot: per employee the measure's main line and the split's lines indented under it,
 * each with its month total and one column per date; Saturdays and Sundays shaded. The All employees block closes it.
 * Memoized: it draws every employee's lines by every date, so it redraws only when the report's data does.
 */
export const HoursByDayTable = memo(function HoursByDayTable({
    rows,
    from,
    to,
    totals,
}: {
    rows: HoursByEmployeeAndDayRow[];
    from: string;
    to: string;
    totals: HoursByEmployeeAndDayLine[];
}) {
    const { displayDecimalScale } = useTimecardPrecision();
    const days = daysOf(from, to, new Date());
    const hours = (seconds: number) => formatReportNumber(seconds / SECONDS_PER_HOUR, displayDecimalScale);
    const block = (key: string | number, label: ReactNode, lines: HoursByEmployeeAndDayLine[]) =>
        lines.map((line, index) => (
            <TableRow key={`${key}-${line.line}`} className={cn(index === 0 && 'font-semibold')}>
                {index === 0 ? (
                    <TableCell rowSpan={lines.length} className={cn(EMPLOYEE_COLUMN, 'align-top')}>
                        {label}
                    </TableCell>
                ) : null}
                <TableCell className={cn(LINE_COLUMN, index > 0 && 'ps-6 font-normal')}>
                    {t(`reporting.hours-line.${line.line}`)}
                </TableCell>
                <TableCell className="text-right tabular-nums">{hours(line.total)}</TableCell>
                {days.map((day) => (
                    <TableCell
                        key={day.key}
                        className={cn('text-right tabular-nums', day.isWeekend && 'bg-surface-subtle')}
                    >
                        {day.isFuture && !line.days[day.key] ? '' : hours(line.days[day.key] ?? 0)}
                    </TableCell>
                ))}
            </TableRow>
        ));
    return (
        <Table className="text-meta">
            <TableHeader>
                <TableRow>
                    <TableHead className={EMPLOYEE_COLUMN}>{t('reporting.column.employee')}</TableHead>
                    <TableHead className={LINE_COLUMN}>{t('reporting.column.hours')}</TableHead>
                    <TableHead className="text-right">{t('reporting.column.month-total')}</TableHead>
                    {days.map((day) => (
                        <TableHead
                            key={day.key}
                            className={cn('text-right tabular-nums', day.isWeekend && 'bg-surface-subtle')}
                        >
                            {format(parseISO(day.key), 'EEE d')}
                        </TableHead>
                    ))}
                </TableRow>
            </TableHeader>
            <TableBody>
                {rows.flatMap((row) =>
                    block(
                        row.employeeId,
                        <div className="flex flex-col gap-0.5">
                            <ReportEmployeeLink employeeId={row.employeeId}>{employeeLabel(row)}</ReportEmployeeLink>
                            {row.coreTeam || row.jobRole ? (
                                <span className="text-foreground-secondary font-normal">
                                    {[row.coreTeam, row.jobRole].filter(Boolean).join(' · ')}
                                </span>
                            ) : null}
                        </div>,
                        row.lines
                    )
                )}
                {block('all', t('reporting.hours-by-employee-and-day.all-employees'), totals)}
            </TableBody>
        </Table>
    );
});
